import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Form, DependentFieldInfo } from 'src/app/base/interfaces/form';
import { UntypedFormControl, UntypedFormGroup, Validators, ValidatorFn, AbstractControl, FormControl } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { PageActionResponse } from 'src/app/base/interfaces/pageActionResponse';
import { MatDialog } from '@angular/material/dialog';
import { InvalidPopupComponent } from '../invalid-popup/invalid-popup.component';
import { ContentService } from 'src/app/base/services/content/content.service';
import { KeyValue } from '@angular/common';
import { FluidpayTokenizerComponent } from 'src/app/easysuite/components/fluidpay-tokenizer/fluidpay-tokenizer.component';

@Component({
    selector: 'app-form',
    templateUrl: './form.component.html',
    styleUrls: ['./form.component.css']
})

export class FormComponent implements OnInit {

    @Input() form: Form;
    @Input() showSubmitButton: boolean = true;
    @Input() parentSubmitButton: Observable<boolean> = null;
    @Input() pageID: number = null;
    @ViewChild(FluidpayTokenizerComponent) hostedFormComponent?: FluidpayTokenizerComponent;

    @Output() formSubmitted = new EventEmitter<Record<number, string>>();
    @Output() pageAction: EventEmitter<PageActionResponse> = new EventEmitter<PageActionResponse>();

    navID;
    formGroup: UntypedFormGroup;
    inputValue: string;
    buttons: any [] = [];

    // Subscribtion for controller fields 
    private ControllerFieldSubscriptions = new Subscription();

    // This array will be used by the html to determine if the field should be displayed or not
    FieldVisibility: { [key: string]: boolean } = {};

    constructor(protected route: ActivatedRoute, protected dialog: MatDialog, protected contentService: ContentService) { }

    ngOnInit() {
        this.navID = this.route.snapshot.data.nav;

        if (this.parentSubmitButton != null) {
            this.parentSubmitButton.subscribe(x => {
                this.submitForm();
            })
        }

        this.formGroup = new UntypedFormGroup({}, {
            updateOn: 'change'
        });

        // Remove fields that were passed in through backend array RemoveFromFormFieldIDs, before building the formGroup.
        // We need to delete it from the form.fields, because in the submitForm() method below
        // the logic will loop through the form.fields and try to get the formGroups value.
        // This will end up breaking if a value in form.fields does not exist in the formGroups.
        for (const FieldID of this.form.RemoveFromFormFieldIDs) {
            delete this.form.fields[FieldID.toString()];
        }

        // key is the field ID
        for (let key in this.form.fields) {
            let field = this.form.fields[key];

            let newControl = new UntypedFormControl();

            if (field.required) newControl.addValidators(Validators.required);
            if (field.value) newControl.setValue(field.value);

            // Disable the field if it is in the uneditableFieldIDs array
            // This array is instantiated on the backend in the FormBase class, and is populated during the OnLoad Function of the page
            if (this.form.UneditableFieldIDs.includes(parseInt(key))) {
                newControl.disable();
            }

            this.formGroup.addControl(key, newControl);

            // Add form control for confirmation fields if needed.
            if (field.format === 'email_with_confirmation' || field.format === 'password_with_confirmation') {
                const confirmKey = `${key}_confirmation`;
                if (!this.formGroup.get(confirmKey)) {
                    this.formGroup.addControl(confirmKey, new FormControl('', []));
                }
            }

            this.FieldVisibility[key] = true;
        }

        // Loop through the array of field IDs that require another field and add the validator to the form group.
        // The key is the field ID that requires another field when it has a value (dependent field id).
        // The value is the field ID that is required when the key has a value (controller field id).
        for (let key in this.form.FieldIDsThatRequireAnotherField) {

            this.formGroup.addValidators(this.FieldARequiresFieldBValidator(key, this.form.FieldIDsThatRequireAnotherField[key]));
        }

        // Go through and group all the controller field values that are linked to each dependent field.
        const DependentFieldsControllerValues: Record<string, DependentFieldInfo[]> = {};

        for (const DependentFieldInfo of this.form.DependentFieldsInfo) {

            const DependentFieldID = DependentFieldInfo.DependentFieldID.toString();

            if (!DependentFieldsControllerValues[DependentFieldID]) {
                DependentFieldsControllerValues[DependentFieldID] = [];
            }

            DependentFieldsControllerValues[DependentFieldID].push(DependentFieldInfo);
        }

        // For each dependent field, subscribe once to each unique controller field.
        for (const DependentFieldID in DependentFieldsControllerValues) {
			
            const DependentFieldControllerValues = DependentFieldsControllerValues[DependentFieldID];

            // Get unique controller field IDs for this dependent field.
            const ControllerFieldIDs = Array.from(
                new Set(DependentFieldControllerValues.map(ControllerValues => ControllerValues.ControllerFieldID.toString()))
            );

            for (const ControllerFieldID of ControllerFieldIDs) {
                const ControllerField = this.formGroup.get(ControllerFieldID);
                
                this.ControllerFieldSubscriptions.add(
                    ControllerField.valueChanges.subscribe(() => {
                        this.updateDependentFieldVisibility(DependentFieldID, DependentFieldControllerValues);
                    })
                );
            }

            // Run once on load
            this.updateDependentFieldVisibility(DependentFieldID, DependentFieldControllerValues);
        }

        this.buttons = Object.values(this.form.Buttons);

        if (this.formGroup.controls['58'] != undefined) {
            this.formGroup.controls['58'].valueChanges.subscribe(x => this.promptEndDate())
        }
    }

    // Only for additional buttons, not submit or cancel button
    additionalFormButtonClick(button){
        if (button.Type === "callback") {
            // We call the contentService service to process the button action
            this.contentService.submitButtonClick(this.navID, this.pageID, button.Label, button.Options).subscribe({
                next: (x) => {
                    /* 
                    * When we are going to make a 'redirect' to the same page we cannot handle it with the Angular router, 
                    * since it only reloads the component, but does not make the request to the Backend again, 
                    * so we must do it with a complete reload
                    */
                    if (x.action === "redirect" && x.pageID == this.pageID) {
                        window.location.reload();
                    } else {
                        this.pageAction.emit(x);
                    }
                },
                error: (err) => console.log(err)
            });
        } else if(button.Type == "link"){
        }
    }

    /**
     * This function is only async because it is used by components that use await.
     * If there are not hosted forms, then the function logic is executed immediately.
     * Currently await is only used by Payroc's ACH component called tokenizer.
     */
    async submitForm(): Promise<void> {
        const confirmationFieldValidation = this.validateConfirmationFields();
        if (!confirmationFieldValidation.isValid) {
            this.dialog.open(InvalidPopupComponent, {
                data: { message: confirmationFieldValidation.message }
            });
            return;
        }
        
        if (!this.formGroup.valid) {
            let list = []
            for (let key in this.form.fields) {
                if (this.formGroup.get(key).invalid) {
                    this.formGroup.get(key).markAsTouched();
                }
                list.push(key)
            }

            if (this.formGroup.invalid) {

                // This variable will hold custom errors (Currently only can be error for FieldARequiresFieldBValidator)
                const errors = this.formGroup.errors;

                // If the errors exists
                if (errors) {

                    // Popop the custom error
                    const popupDialog = this.dialog.open(InvalidPopupComponent, {
                        data: {
                            message: Object.values(errors)[0]
                        }
                    });
                } 
                // Else the only other error is if the user submitted the form without filling out all required fields
                else {
                    const popupDialog = this.dialog.open(InvalidPopupComponent, {
                        data: {
                            message: "All highlighted fields are required in their proper format."
                        }
                    });
                }
            }
            return
        }

        //form is valid, submit it
        let fieldsToSubmit: Record<number | string, string> = {};
        for (let key in this.form.fields) {

            // If the field has a value
            if (this.formGroup.get(key)?.value) {
                fieldsToSubmit[key] = this.formGroup.get(key).value;
            }
        }

        // If there is a 3rd party form on the page.
        if (this.form.HostedForm) {

            // NOTE: In future if we have other 3rd party forms, we can add conditional to do their specific logic.
            if (this.form.HostedForm.Provider == "Fluidpay") {

                // Wait for Fluidpay's result before submitting to the backend.
                const result = await this.hostedFormComponent.tokenize();

                // Display the error and stop if tokenization failed.
                if (result.success === 0) {
                    this.dialog.open(InvalidPopupComponent, {
                        data: {
                            message: result.errorMessage
                        }
                    });

                    return;
                }

                // If we have reached here then we have a successful token so add it to the fields so that the backend can access it.
                fieldsToSubmit.SingleUseToken = result.token;
            }
        }
            
        this.formSubmitted.emit(fieldsToSubmit);
    }

    cancelForm() {

        // If the BE OnLoad Request set a value for the CancelButtonPageActionResponse variable
        if (this.form.CancelButtonPageActionResponse) {

            // Then emit the page action response value stored in variable through the pageAction property.
            // NOTE: The pageAction event inside of easysuite-superstructure.component.html file is bound to handleActionResponse and listens for this event to fire,
            // so that we can use the handleActionResponse function inside of SuperstructureBaseComponent class.
            this.pageAction.emit(this.form.CancelButtonPageActionResponse);
            return;
        }
        // Else no value, so return to the previous page in the browser history
        else {
            window.history.back();
        }
    }

    validateConfirmationFields(): { isValid: boolean; message?: string } {        
        const fieldsWithConfirmation = Object.keys(this.formGroup.controls)
            .filter(key => key.endsWith('_confirmation'))
            .map(confirmationKey => confirmationKey.replace('_confirmation', ''));
        
        for (const fieldKey of fieldsWithConfirmation) {
            const field = this.form.fields[fieldKey];
            
            if (field && (field.format === 'email_with_confirmation' || field.format === 'password_with_confirmation')) {
                const mainControl = this.formGroup.get(fieldKey);
                const confirmationControl = this.formGroup.get(fieldKey + '_confirmation');
                
                if (mainControl && confirmationControl) {
                    const mainValue = mainControl.value;
                    const confirmValue = confirmationControl.value;
                    
                    // If is email field, validate email formats
                    if (field.format === 'email_with_confirmation') {
                        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

                        if (mainValue && !emailRegex.test(mainValue)) {
                            return { isValid: false, message: 'Please enter a valid email address.' };
                        }
                        if (confirmValue && !emailRegex.test(confirmValue)) {
                            return { isValid: false, message: 'Please enter a valid email in the confirmation field.' };
                        }
                    }
                    
                    // Check if both values match (for both email and password)
                    if (mainValue && confirmValue && mainValue !== confirmValue) {
                        return { isValid: false, message: "The confirmation doesn't match. Please check both fields." };
                    }
                }
            }
        }
        
        return { isValid: true };
    }

    fieldValidate(field, value: string): void {
        // The phone number comes from the backend as "phone number", so I'm doing a replace of the empty spaces so that it matches the keys of the formatPatterns object
        const type = field.value.format
        const normalizedFieldType = type.replace(/\s+/g, '_');

        // These are the regular expressions that each format will allow
        const formatPatterns: { [key: string]: RegExp } = {
          number: /^[0-9]*$/,
          phone_number: /^\(\d{3}\)-\d{3}-\d{4}$/, 
          price: /^[0-9]*\.?[0-9]{0,2}$/
        };

        // Error Message for each format
        const errorMessages: { [key: string]: string } = {
            number: 'The field must contain only numbers.',
            phone_number: 'The phone number must be in the format (XXX)-XXX-XXXX.',
            price: 'The field must contain only numbers.'
        };
    
    
        const pattern = formatPatterns[normalizedFieldType];
        if (pattern && !pattern.test(value)) {
            const popupDialog = this.dialog.open(InvalidPopupComponent, {
                data: {
                    message: `Invalid format: ${errorMessages[normalizedFieldType]}`
                }
            });

            // Reset the input value and update the corresponding FormControl in the FormGroup
            this.inputValue = '';
            this.formGroup.get(field.key)?.setValue(this.inputValue);

            // The popup will close automatically after 3 seconds
            setTimeout(() => {
                popupDialog.close();
            }, 3000);
        }

    }

    asIsOrder(a, b) {
        return a.value.fieldOrder > b.value.fieldOrder ? 1 : -1;
    }

    promptEndDate(): any {
        if (this.form.fields['58'] && this.form.fields['59']) {
            if (this.form.fields['58'] != undefined) {
                return this.formGroup.controls['59'].setValue(this.formGroup.controls['58'].getRawValue());
            }
        }
    }

    firstLabel(): void {
        for (let key in this.form.fields) {
            if (this.formGroup.get(key).markAsTouched) {
                let label = this.form.fields[key].label
            }
        }
    }

    // Field Validation functions //

    /*
    This field validation function will return a function that will check if FieldA has a value and FieldB does not, if that is the case then it will return an error.
    */
    FieldARequiresFieldBValidator(FieldA_ID, FieldB_ID): ValidatorFn {
        return (formGroup: AbstractControl): { [key: string]: any } | null => {

            const FieldA = formGroup.get(FieldA_ID);
            const FieldB = formGroup.get(FieldB_ID);

            // If fieldA has a value and fieldB does not, return an error
            if (FieldA.value && !FieldB.value) {

                // Get the labels of the fields to use in the error message
                const FieldA_Label = this.form.fields[FieldA_ID].label;
                const FieldB_Label = this.form.fields[FieldB_ID].label;

                return { FieldARequiresFieldBError: `"${FieldB_Label}" still needs a value because "${FieldA_Label}" has a value.`};
            }
            return null;
        };
    }

    updateDependentFieldVisibility(DependentFieldID: string, ControllerFieldValues: DependentFieldInfo[]): void {

        const DependentField = this.formGroup.get(DependentFieldID);
        let ShouldShowDependentField = false;

        // Check every controller field value for this dependent field.
        for (const ControllerFieldValue of ControllerFieldValues) {

            const ControllerFieldID = ControllerFieldValue.ControllerFieldID.toString();
            const ControllerField = this.formGroup.get(ControllerFieldID);

            const ControllerValue = ControllerField.value;
            const RequiredOptionValue = ControllerFieldValue.ControllerFieldOptionValue;

            // Multi-checkbox field value
            if (Array.isArray(ControllerValue)) {
                if (ControllerValue.includes(RequiredOptionValue)) {
                    ShouldShowDependentField = true;
                    break;
                }
            }
            // Radio/dropdown/single-value field
            else {
                if (ControllerValue === RequiredOptionValue) {
                    ShouldShowDependentField = true;
                    break;
                }
            }
        }

        this.FieldVisibility[DependentFieldID] = ShouldShowDependentField;

        if (ShouldShowDependentField) {

            // If the dependent field is required, add the required validator back.
            if (this.form.fields[DependentFieldID].required) {
                DependentField.addValidators(Validators.required);
            }

            // Restore original value only if the field is currently empty.
            if (
                DependentField.value == null &&
                this.form.fields[DependentFieldID].value != null
            ) {
                DependentField.setValue(this.form.fields[DependentFieldID].value, {
                    emitEvent: false
                });
            }
        } else {
            // If the dependent field is required, remove the required validator while hidden.
            if (this.form.fields[DependentFieldID].required) {
                DependentField.removeValidators(Validators.required);
            }

            // Clear hidden field value.
            DependentField.setValue(null, {
                emitEvent: false
            });
        }

        // Recalculate validation after adding or removing the required Validator above
        DependentField.updateValueAndValidity({
            emitEvent: false
        });
    }

    ngOnDestroy(): void {
        this.ControllerFieldSubscriptions.unsubscribe();
    }
    
}


/**
 * Comparator function for sorting key-value pairs based on the 'order' property.
 *
 * This function is designed to be used as a comparator for sorting key-value pairs,
 * where each value has an 'order' property. The sorting is based on numerical comparison
 * of the 'order' properties.
 *
 * @param a - The first key-value pair.
 * @param b - The second key-value pair.
 * @returns A negative number if a should come before b, a positive number if b should come
 *          before a, and 0 if their 'order' properties are equal or undefined.
 * 
 * This function should be used in the html file to sort the fields in the order they were created (Each field type has their own specific html file)
 */
export function originalOrder (a: KeyValue<string, any>, b: KeyValue<string, any>): number  {

    if (a.value.order !== undefined && b.value.order !== undefined) {
        return a.value.order - b.value.order;
    }
    return 0;
}
